import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { NavigateFunction } from 'react-router';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { useCheckHeader } from 'Src/feature/check/useCheckHeader';
import useRoute from 'Src/routes/useRoute';

import { LANPrintJobFailure } from './types';
import { NotificationUI } from './useNotification';

import styles from './PrintFailureToastNotification.module.scss';

const sectionId = 'PrintFailureToastNotification';

interface LANPrintFailureToastNotificationProps {
  notification: NotificationUI<LANPrintJobFailure>;
  onAction?: () => void;
  onNavigate?: NavigateFunction;
}

const LANPrintFailureToastNotification: FC<LANPrintFailureToastNotificationProps> = ({
  notification,
  onAction,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const { data } = notification;
  const { crumbs, route } = useRoute();
  const checkHeader = useCheckHeader.query.collection();

  const i18nKey = data.checkId ? 'notification.printFailure.unhandledFailedAtNav' : 'notification.printFailure.genericLANPrintFailed';
  const deviceName = data.primaryPrinter.name;
  
  const checkHeaderFromNotification = checkHeader.data && data.checkId ? checkHeader.data.map.get(data.checkId) : undefined;

  return (
    <Flex
      horizontal
      alignItems="center"
      justifyContent="space-between"
      className={styles.root}
    >
      <Flex gap="sm">
        <Copy
          variant="body"
          weight="md"
          value={checkHeaderFromNotification?.label ? t('notification.printFailure.groupTitleCheck', { checkLabel: checkHeaderFromNotification?.label }) : t('notification.printFailure.groupTitle')}
        />
        <Copy
          variant="body"
          size="sm"
          weight="regular"
          // value={t(i18nKey, {
          //   checkLabel: checkHeaderFromNotification?.label ?? t('notification.printFailure.unknownCheckNumber'),
          //   deviceName,
          //   errStr: data.error,
          //   printJobType: data.jobType,
          // })}
        >
          <Trans
            data-testid={sectionId}
            i18nKey={i18nKey}
            values={{
              checkLabel: checkHeaderFromNotification?.label || t('notification.printFailure.unknownCheckNumber'),
              deviceName: deviceName,
              errStr: data.error,
              printJobType: data.jobType,
            }}
            components={{
              primary: <Copy color="textSecondary" variant="body" size="sm" weight="regular" />,
              navigate: (
                <Copy
                  color="secondary"
                  variant="body"
                  size="sm"
                  weight="regular"
                  onClick={() => {
                    if (onNavigate && data.checkId) onNavigate(crumbs.pos.check.main.interpolate({
                      checkId: data.checkId,
                      // start w/ single select mode for check item
                      itemSelect: route.params.value.one
                    }));
                  }}
                />
              ),
            }}
          />
        </Copy>
      </Flex>
      <CTA
        color="textPrimary"
        label={t('notification.printFailure.retry')}
        onClick={() => {
          const evt = new CustomEvent('notify.TerminalLANPrintJob', {
            detail: data,
          });
          document.dispatchEvent(evt);
          if (onAction) onAction();
        }}
      />
    </Flex>
  );
};

export type { LANPrintFailureToastNotificationProps };
export { LANPrintFailureToastNotification, sectionId };
